import {Avatar, Card, DataStatus, Label, Separator} from 'platform/components';
import {Box, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {LocaleDateTimeFormat, useDateTimeFormatter} from 'platform/locale';

import {Fragment, ReactNode, useState} from 'react';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {GetServiceCasesByBusinessCaseApiResponse} from '@dms/api/sales';
import {
  useGetServiceCaseServiceRepairApiQuery,
  useGetServiceCheckInQuery,
  useGetServiceHandOverQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {EMPTY_PLACEHOLDER, queryParams, useStateFlag} from '@dms/shared';

import {buildArray, composePath, Nullish, parseDate} from 'shared/web';

interface ServiceCaseCardProps {
  serviceCase: GetServiceCasesByBusinessCaseApiResponse[number];
  isInitiallyOpen?: boolean;
  returnUrl?: string;
}

export function ServiceCaseCard(props: ServiceCaseCardProps) {
  const [isExpanded, setIsExpanded] = useState(props.isInitiallyOpen ?? false);
  const formatDateTime = useDateTimeFormatter();
  const stateFlag = useStateFlag(props.serviceCase.state as Parameters<typeof useStateFlag>[0]);

  const {
    data: serviceRepair,
    isLoading: isServiceRepairLoading,
    isError: isServiceRepairError,
  } = useGetServiceCaseServiceRepairApiQuery(
    {serviceCaseId: props.serviceCase.serviceCaseId},
    {skip: !isExpanded}
  );
  const {
    data: checkin,
    isLoading: isCheckinLoading,
    isError: isCheckinError,
  } = useGetServiceCheckInQuery(
    {serviceCaseId: props.serviceCase.serviceCaseId},
    {skip: !isExpanded}
  );
  const {
    data: handover,
    isLoading: isHandoverLoading,
    isError: isHandoverError,
  } = useGetServiceHandOverQuery(
    {serviceCaseId: props.serviceCase.serviceCaseId},
    {skip: !isExpanded}
  );

  const isLoading = isServiceRepairLoading || isCheckinLoading || isHandoverLoading;
  const isError = isServiceRepairError || isCheckinError || isHandoverError;

  const detailPath = composePath(workshopRoutes.serviceCaseDetail, {
    params: {id: props.serviceCase.serviceCaseId},
    queryParams: props.returnUrl
      ? {[queryParams.RETURN_URL]: props.returnUrl, [queryParams.REDIRECT_SOURCE]: 'business-case'}
      : undefined,
  });

  const getFormattedDate = (format: LocaleDateTimeFormat, value: string | Nullish) => {
    if (isNil(value)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDateTime(format, parseDate(value));
  };

  const createdAt = formatDateTime('dateTimeShort', parseDate(props.serviceCase.createdAt));
  const checkinDateTime = getFormattedDate(
    'dateTimeShort',
    checkin?.completedDate ?? checkin?.scheduledDate
  );
  const handoverDateTime = getFormattedDate(
    'dateTimeShort',
    handover?.completedDate ?? handover?.scheduledDate
  );
  const checkinAdvisor = serviceRepair?.checkin?.employee?.name ?? EMPTY_PLACEHOLDER;
  const handoverAdvisor = serviceRepair?.handover?.employee?.name ?? EMPTY_PLACEHOLDER;

  const activities = serviceRepair?.activities?.map((activity) => ({
    date: getFormattedDate('dateShort', activity?.startAt),
    start: getFormattedDate('timeShort', activity?.startAt),
    end: getFormattedDate('timeShort', activity?.endAt),
    mechanic: activity?.employee?.name ?? EMPTY_PLACEHOLDER,
    name: activity?.name ?? EMPTY_PLACEHOLDER,
    competence: activity?.competence?.name ?? EMPTY_PLACEHOLDER,
  }));

  const carWashes = serviceRepair?.carWashes?.map((wash) => ({
    date: getFormattedDate('dateShort', wash?.startAt),
    start: getFormattedDate('timeShort', wash?.startAt),
    end: getFormattedDate('timeShort', wash?.endAt),
    programName: wash?.programName ?? EMPTY_PLACEHOLDER,
    estimatedTime: wash?.estimatedTime?.toFixed(2) ?? EMPTY_PLACEHOLDER,
  }));

  const activitiesSection = isNotNilOrEmpty(activities) ? (
    <HStack spacing={4} align="center" width="100%">
      <Avatar variant="square" icon="sidebar/workshop_service_orders" />
      <VStack width="100%">
        <HStack spacing={4}>
          <Box flex={1}>
            <Label>{i18n.t('entity.serviceScheduler.lables.requestDate')}</Label>
          </Box>
          <Box flex={1}>
            <Label>{i18n.t('entity.serviceScheduler.lables.responsibleMechanic')}</Label>
          </Box>
          <Box flex={1}>
            <Label>{i18n.t('entity.serviceScheduler.lables.requestName')}</Label>
          </Box>
          <Box flex={1}>
            <Label>{i18n.t('entity.serviceScheduler.lables.competence')}</Label>
          </Box>
        </HStack>
        {activities?.map((activity) => (
          <HStack spacing={4} key={`${activity.date}-${activity.start}-${activity.end}`}>
            <Box flex={1}>
              <Text>{`${activity.date} ${activity.start} - ${activity.end}`}</Text>
            </Box>
            <Box flex={1}>
              <Text>{activity.mechanic}</Text>
            </Box>
            <Box flex={1}>
              <Text>{activity.name}</Text>
            </Box>
            <Box flex={1}>
              <Text>{activity.competence}</Text>
            </Box>
          </HStack>
        ))}
      </VStack>
    </HStack>
  ) : null;

  const carWashesSection = isNotNilOrEmpty(carWashes) ? (
    <HStack spacing={4} align="center" width="100%">
      <Avatar variant="square" icon="custom/carwash" />
      <VStack width="100%">
        <HStack spacing={4}>
          <Box flex={1}>
            <Label>{i18n.t('entity.serviceScheduler.lables.carwashDate')}</Label>
          </Box>
          <Box flex={1}>
            <Label>{i18n.t('entity.serviceScheduler.lables.carwashProgram')}</Label>
          </Box>
          <Box flex={1}>
            <Label>{i18n.t('entity.serviceScheduler.lables.carwashTime')}</Label>
          </Box>
          <Space fillAvailable />
        </HStack>
        {carWashes?.map((wash) => (
          <HStack spacing={4} key={`${wash.date}-${wash.start}-${wash.end}`}>
            <Box flex={1}>
              <Text>{`${wash.date} ${wash.start} - ${wash.end}`}</Text>
            </Box>
            <Box flex={1}>
              <Text>{wash.programName}</Text>
            </Box>
            <Box flex={1}>
              <Text>{wash.estimatedTime}</Text>
            </Box>
            <Space fillAvailable />
          </HStack>
        ))}
      </VStack>
    </HStack>
  ) : null;

  const middleSections = buildArray<{id: string; node: ReactNode}>()
    .when(isNotNilOrEmpty(activities), {id: 'activities', node: activitiesSection})
    .when(isNotNilOrEmpty(carWashes), {id: 'car-washes', node: carWashesSection});

  return (
    <Card
      title={i18n.t('page.workshop.labels.serviceCase')}
      flags={stateFlag ? [stateFlag] : undefined}
      parameters={[props.serviceCase.number, `${i18n.t('general.labels.created')}: ${createdAt}`]}
      actions={[
        {
          type: 'button',
          variant: 'link',
          title: i18n.t('entity.workshop.actions.openServiceCase'),
          rightIcon: 'action/open_in_new',
          onClick: () => window.open(detailPath, '_blank'),
        },
      ]}
      isExpandable
      isExpanded={isExpanded}
      onExpandButtonClick={() => setIsExpanded((prev) => !prev)}
    >
      <DataStatus isLoading={isLoading} isError={isError}>
        <VStack spacing={4}>
          <Box padding={4} backgroundColor="palettes.blue.10.100" borderRadius="medium">
            <HStack spacing={4} align="center">
              <Avatar
                variant="square"
                color="palettes.blue.20.100"
                iconColor="palettes.blue.80.100"
                icon="navigation/arrow_upward"
              />
              <Box flex={1}>
                <Label>{i18n.t('entity.serviceScheduler.lables.checkinDate')}</Label>
                <Text>{checkinDateTime}</Text>
              </Box>
              <Box flex={1}>
                <Label>{i18n.t('entity.serviceScheduler.lables.serviceAdvisor')}</Label>
                <Text>{checkinAdvisor}</Text>
              </Box>
              <Space fillAvailable />
              <Space fillAvailable />
            </HStack>
          </Box>
          <Show when={isNotNilOrEmpty(middleSections)}>
            <Box paddingHorizontal={4}>
              {middleSections.map((section, index) => (
                <Fragment key={section.id}>
                  <Show when={index > 0}>
                    <Separator />
                  </Show>
                  {section.node}
                </Fragment>
              ))}
            </Box>
          </Show>
          <Box padding={4} backgroundColor="palettes.green.10.100" borderRadius="medium">
            <HStack spacing={4} align="center">
              <Avatar
                variant="square"
                color="palettes.green.20.100"
                iconColor="palettes.green.80.100"
                icon="navigation/arrow_downward"
              />
              <Box flex={1}>
                <Label>{i18n.t('entity.serviceScheduler.lables.handoverDate')}</Label>
                <Text>{handoverDateTime}</Text>
              </Box>
              <Box flex={1}>
                <Label>{i18n.t('entity.serviceScheduler.lables.serviceAdvisor')}</Label>
                <Text>{handoverAdvisor}</Text>
              </Box>
              <Space fillAvailable />
              <Space fillAvailable />
            </HStack>
          </Box>
        </VStack>
      </DataStatus>
    </Card>
  );
}
